// ============================================================
// Section Boutique (Statistiques) : fonctions pures, sans I/O ni store
// ============================================================
// Données du satellite (route /api/stats/storepoints/shop, 1.0.33) : achats de la boutique en jeu (RecubeBoutiqueMod)
// lus dans l'historique des points boutique. L'historique ne porte que l'identifiant de chaque produit : les noms, les
// catégories (sections du magasin) et les prix viennent de la config de la boutique de l'image (cosmetic.config.json,
// sections ShopSettings et CosmeticSettings), lue par le panel quand le grade y a accès. Sans elle, les produits
// gardent leur identifiant et leur catégorie est devinée d'après lui.

import type { ShopDayDTO, ShopKind, ShopProductDTO } from '@/services/api.service';

/** Version du satellite qui sert la section : la page le dit face à un satellite plus ancien. */
export const SHOP_SATELLITE_VERSION = '1.0.33';

/** Fichier de config de RecubeBoutiqueMod dans les images, et ses deux sections lues (jamais ApiSettings). */
export const SHOP_CONFIG_FILE = 'cosmetic.config.json';
export const SHOP_CONFIG_KEYS = ['ShopSettings', 'CosmeticSettings'] as const;

// ---------- Textes ----------

/** Retire les codes de couleur de Minecraft (« Clé §5Améthyste Box§r x5 » devient « Clé Améthyste Box x5 »). */
export function stripCodes(text: string | null | undefined): string {
  return (text ?? '').replace(/§./g, '').replace(/\s+/g, ' ').trim();
}

/** Produit sans nom connu : « key_amethyst_x5 » devient « Key amethyst x5 ». */
export function productLabel(product: string): string {
  const words = product.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : product;
}

/** Points boutique : « 8 000 PB », ou la monnaie du serveur (« 8 000 NGCoins »). */
export function formatPoints(n: number, currency?: string | null): string {
  return `${Math.round(n).toLocaleString('fr-FR')} ${currencyUnit(currency)}`;
}

/** Unité des points : « PB », sauf une monnaie nommée par la config de la boutique (NGCoins sur NationsGlory). */
export function currencyUnit(currency?: string | null): string {
  const c = stripCodes(currency);
  return c && c.toLowerCase() !== 'points boutique' ? c : 'PB';
}

/** Libellé tout en capitales de la config (« CLÉS ») mis en casse de phrase (« Clés »). */
function sentenceCase(text: string): string {
  if (!text || text !== text.toUpperCase()) return text;
  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Type d'un cosmétique, comme RecubeBoutiqueMod l'écrit (CosmeticType.GetDisplayName). */
const COSMETIC_TYPES: Record<string, string> = {
  cape: 'Cape',
  wearable_head: 'Accessoire de tête',
  wearable_body: 'Accessoire de dos',
  wearable_suit: 'Costume',
  kill_animation: 'Animation de mort',
  kill_message: 'Message de mort',
  emote: 'Emote',
};

export function cosmeticTypeLabel(type: string): string {
  return COSMETIC_TYPES[type] ?? productLabel(type);
}

const RARITIES: Record<string, string> = {
  common: 'Commun',
  rare: 'Rare',
  epic: 'Épique',
  legendary: 'Légendaire',
  limited: 'Limité',
};

/** Rareté d'un cosmétique (nom de l'énumération du mod, ou son numéro). */
export function rarityName(raw: unknown): string | null {
  if (typeof raw === 'number') return Object.values(RARITIES)[raw] ?? null;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  return RARITIES[raw.trim().toLowerCase()] ?? raw.trim();
}

// ---------- Catalogue (config de la boutique de l'image) ----------

export interface CatalogProduct {
  name: string;
  /** Catégorie de sa section (CategoryId : keys, ranks, lucky_blocks...). */
  category: string;
  /** Nom de sa section dans le magasin (« BOX AMÉTHYSTE »). */
  section: string | null;
  price: number | null;
}

export interface CatalogCosmetic {
  name: string;
  type: string;
  rarity: string | null;
  price: number | null;
}

export interface ShopCatalog {
  products: Map<string, CatalogProduct>;
  cosmetics: Map<string, CatalogCosmetic>;
  packs: Map<string, { name: string; price: number | null }>;
  /** Catégories du magasin nommées par la config (ShopSettings.Categories). */
  categoryNames: Map<string, string>;
  /** Monnaie des écrans de la boutique (ShopSettings.CurrencyName), null par défaut. */
  currency: string | null;
}

type Json = Record<string, unknown>;

/** Valeur d'un champ quelle que soit sa casse (le mod lit sa config sans tenir compte de la casse). */
function field(obj: unknown, name: string): unknown {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
  const o = obj as Json;
  if (name in o) return o[name];
  const lower = name.toLowerCase();
  const key = Object.keys(o).find((k) => k.toLowerCase() === lower);
  return key === undefined ? undefined : o[key];
}

function list(obj: unknown, name: string): unknown[] {
  const v = field(obj, name);
  return Array.isArray(v) ? v : [];
}

function text(obj: unknown, name: string): string {
  const v = field(obj, name);
  return typeof v === 'string' ? v.trim() : '';
}

function price(obj: unknown, name = 'Price'): number | null {
  const v = field(obj, name);
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/** Listes de cosmétiques de CosmeticSettings et leur type. */
const COSMETIC_LISTS: [string, string][] = [
  ['Capes', 'cape'],
  ['WearableHeads', 'wearable_head'],
  ['WearableBodies', 'wearable_body'],
  ['WearableSuits', 'wearable_suit'],
  ['KillAnimations', 'kill_animation'],
  ['KillMessages', 'kill_message'],
  ['Emotes', 'emote'],
];

/**
 * Catalogue de la boutique lu dans les sections de cosmetic.config.json, null sans ShopSettings ni CosmeticSettings.
 * Un produit d'une section sans nom prend celui de son cosmétique, puis son identifiant ; sans prix, celui du
 * cosmétique (comme le mod).
 */
export function parseCatalog(sections: Record<string, unknown> | null | undefined): ShopCatalog | null {
  if (!sections) return null;
  const shop = field(sections, 'ShopSettings');
  const cosmeticSettings = field(sections, 'CosmeticSettings');
  if (!shop && !cosmeticSettings) return null;

  const cosmetics = new Map<string, CatalogCosmetic>();
  for (const [listName, type] of COSMETIC_LISTS) {
    for (const c of list(cosmeticSettings, listName)) {
      const id = text(c, 'Id');
      if (!id || cosmetics.has(id)) continue;
      cosmetics.set(id, { name: stripCodes(text(c, 'DisplayName')) || productLabel(id), type, rarity: rarityName(field(c, 'Rarity')), price: price(c) });
    }
  }

  const products = new Map<string, CatalogProduct>();
  for (const s of list(shop, 'Sections')) {
    const category = text(s, 'CategoryId');
    const section = sentenceCase(stripCodes(text(s, 'DisplayName'))) || null;
    for (const p of list(s, 'Products')) {
      const id = text(p, 'Id');
      if (!id || products.has(id)) continue;
      const cosmetic = cosmetics.get(text(p, 'CosmeticId'));
      products.set(id, {
        name: stripCodes(text(p, 'DisplayName')) || cosmetic?.name || productLabel(id),
        category: category || 'other',
        section,
        price: price(p) ?? cosmetic?.price ?? null,
      });
    }
  }

  const packs = new Map<string, { name: string; price: number | null }>();
  for (const p of list(shop, 'Packs')) {
    const id = text(p, 'Id');
    if (id && !packs.has(id)) packs.set(id, { name: stripCodes(text(p, 'DisplayName')) || productLabel(id), price: price(p) });
  }

  const categoryNames = new Map<string, string>();
  for (const c of list(shop, 'Categories')) {
    const id = text(c, 'Id');
    const label = stripCodes(text(c, 'DisplayName'));
    // Une clé de langue (« recubeboutique.category.keys ») n'est pas un libellé.
    if (id && label && !/^[\w-]+(\.[\w-]+)+$/.test(label)) categoryNames.set(id, sentenceCase(label));
  }

  const currency = stripCodes(text(shop, 'CurrencyName')) || null;
  return { products, cosmetics, packs, categoryNames, currency };
}

// ---------- Catégories ----------

/** Catégories connues, dans l'ordre fixe des couleurs (la couleur suit la catégorie, jamais son rang). */
export const CATEGORY_ORDER = ['cosmetics', 'packs', 'keys', 'ranks', 'lucky_blocks'] as const;
export const OTHER_CATEGORY = 'other';

const CATEGORY_LABELS: Record<string, string> = {
  cosmetics: 'Cosmétiques',
  packs: 'Packs de cosmétiques',
  keys: 'Clés et box',
  ranks: 'Grades',
  lucky_blocks: 'Lucky blocks',
  other: 'Autres',
};

const KEY_WORD = /(^|[_:-])(key|keys|cle|cles|box|lootbox)([_:-]|$)/i;
const RANK_WORD = /(^|[_:-])(rank|ranks|grade|grades|upgrade)([_:-]|$)/i;
const LUCKY_WORD = /(^|[_:-])lucky/i;
const COSMETIC_WORD = /^(cape|hat|back|suit|wing|wings|kill_animation|kill_message|emote|pet)([_:-]|$)|(^|[_:-])suit$/i;

/**
 * Catégorie d'un achat : pack et cosmétique du jour d'office ; un produit, celle de sa section dans la config (un
 * cosmétique de la liste du magasin : Cosmétiques) ; sans config, devinée d'après son identifiant.
 */
export function categoryOf(kind: ShopKind, id: string, catalog: ShopCatalog | null): string {
  if (kind === 'pack') return 'packs';
  if (kind === 'cosmetic') return 'cosmetics';
  const product = catalog?.products.get(id);
  if (product) return product.category;
  if (catalog?.cosmetics.has(id)) return 'cosmetics';
  if (KEY_WORD.test(id)) return 'keys';
  if (RANK_WORD.test(id)) return 'ranks';
  if (LUCKY_WORD.test(id)) return 'lucky_blocks';
  if (COSMETIC_WORD.test(id)) return 'cosmetics';
  return OTHER_CATEGORY;
}

export function categoryLabel(id: string, catalog: ShopCatalog | null): string {
  return CATEGORY_LABELS[id] ?? catalog?.categoryNames.get(id) ?? productLabel(id);
}

/** Les catégories présentes, dans l'ordre fixe : connues d'abord, puis celles de la config par nom, Autres en dernier. */
export function orderCategories(ids: Iterable<string>): string[] {
  const all = [...new Set(ids)];
  const known = CATEGORY_ORDER.filter((c) => all.includes(c));
  const extra = all.filter((c) => !(CATEGORY_ORDER as readonly string[]).includes(c) && c !== OTHER_CATEGORY).sort();
  return [...known, ...extra, ...(all.includes(OTHER_CATEGORY) ? [OTHER_CATEGORY] : [])];
}

/**
 * Couleur d'une catégorie, prise dans les teintes validées des statistiques (--job-1 à --job-8) dans l'ordre fixe des
 * catégories connues, puis des autres par nom ; Autres en gris. Une catégorie garde sa couleur quel que soit le
 * filtre.
 */
export function categoryColor(id: string, present: readonly string[]): string {
  if (id === OTHER_CATEGORY) return 'var(--job-other)';
  const known = (CATEGORY_ORDER as readonly string[]).indexOf(id);
  if (known >= 0) return `var(--job-${known + 1})`;
  const extra = [...new Set(present)].filter((c) => !(CATEGORY_ORDER as readonly string[]).includes(c) && c !== OTHER_CATEGORY).sort();
  const slot = CATEGORY_ORDER.length + extra.indexOf(id) + 1;
  return extra.includes(id) && slot <= 8 ? `var(--job-${slot})` : 'var(--job-other)';
}

// ---------- Produits ----------

export interface ShopProductView {
  /** Clé d'affichage : un cosmétique acheté des deux façons (produit du magasin, cosmétique du jour) n'a qu'une ligne. */
  key: string;
  id: string;
  kind: ShopKind;
  name: string;
  /** Section du magasin, type et rareté d'un cosmétique. */
  detail: string | null;
  category: string;
  purchases: number;
  refunds: number;
  buyers: number;
  buyerIds: number[];
  spentSeasonal: number;
  spentClassic: number;
  /** Points rendus. */
  refunded: number;
  /** Points gardés : dépensés moins rendus. */
  net: number;
  keys: number | null;
  /** Prix de la config, sinon celui du dernier achat. */
  price: number | null;
  lastAt: number | null;
}

/** Nom et détail d'un produit, d'après la config de la boutique quand elle est lue. */
export function describe(kind: ShopKind, id: string, catalog: ShopCatalog | null): { name: string; detail: string | null; price: number | null } {
  if (kind === 'pack') {
    const pack = catalog?.packs.get(id);
    return { name: pack?.name ?? productLabel(id), detail: 'Pack de cosmétiques', price: pack?.price ?? null };
  }
  const product = kind === 'product' ? catalog?.products.get(id) : undefined;
  const cosmetic = catalog?.cosmetics.get(id);
  if (product) return { name: product.name, detail: product.section, price: product.price };
  if (cosmetic) {
    const detail = [cosmeticTypeLabel(cosmetic.type), cosmetic.rarity].filter(Boolean).join(', ');
    return { name: cosmetic.name, detail: detail || null, price: cosmetic.price };
  }
  return { name: productLabel(id), detail: null, price: null };
}

/**
 * Lignes de produits à afficher : nom, détail et catégorie d'après la config, un cosmétique acheté des deux façons
 * réuni en une ligne (achats et points additionnés, acheteurs sans doublon). Ordre : points gardés décroissants.
 */
export function productViews(rows: readonly ShopProductDTO[], catalog: ShopCatalog | null): ShopProductView[] {
  const byKey = new Map<string, ShopProductView>();
  for (const r of rows) {
    const category = categoryOf(r.kind, r.product, catalog);
    const key = category === 'cosmetics' && r.kind !== 'pack' ? `cosmetic:${r.product}` : r.key;
    const refunded = r.refundSeasonal + r.refundClassic;
    const spent = r.spentSeasonal + r.spentClassic;
    const prev = byKey.get(key);
    if (!prev) {
      const d = describe(r.kind, r.product, catalog);
      byKey.set(key, {
        key, id: r.product, kind: r.kind, name: d.name, detail: d.detail, category,
        purchases: r.purchases, refunds: r.refunds, buyers: r.buyerIds.length, buyerIds: [...r.buyerIds],
        spentSeasonal: r.spentSeasonal, spentClassic: r.spentClassic, refunded, net: spent - refunded,
        keys: r.keys ?? null, price: d.price ?? r.lastPrice ?? null, lastAt: r.lastAt ?? null,
      });
      continue;
    }
    const ids = [...new Set([...prev.buyerIds, ...r.buyerIds])].sort((a, b) => a - b);
    byKey.set(key, {
      ...prev,
      purchases: prev.purchases + r.purchases,
      refunds: prev.refunds + r.refunds,
      buyerIds: ids,
      buyers: ids.length,
      spentSeasonal: prev.spentSeasonal + r.spentSeasonal,
      spentClassic: prev.spentClassic + r.spentClassic,
      refunded: prev.refunded + refunded,
      net: prev.net + spent - refunded,
      keys: prev.keys == null && r.keys == null ? null : (prev.keys ?? 0) + (r.keys ?? 0),
      price: prev.price ?? r.lastPrice ?? null,
      lastAt: Math.max(prev.lastAt ?? 0, r.lastAt ?? 0) || null,
    });
  }
  return [...byKey.values()].sort((a, b) => b.net - a.net || b.purchases - a.purchases || a.name.localeCompare(b.name, 'fr'));
}

// ---------- Catégories : totaux ----------

export interface ShopCategoryView {
  id: string;
  label: string;
  color: string;
  purchases: number;
  refunds: number;
  /** Acheteurs distincts de la catégorie (un joueur qui achète deux produits compte une fois). */
  buyers: number;
  /** Produits vendus (au moins un achat gardé). */
  products: number;
  spent: number;
  refunded: number;
  net: number;
  /** Part des points gardés de la période (0 à 1). */
  share: number;
}

export function categoryViews(products: readonly ShopProductView[], catalog: ShopCatalog | null): ShopCategoryView[] {
  const ids = orderCategories(products.map((p) => p.category));
  const totalNet = products.reduce((s, p) => s + Math.max(0, p.net), 0);
  return ids.map((id) => {
    const rows = products.filter((p) => p.category === id);
    const buyers = new Set(rows.flatMap((p) => p.buyerIds));
    const net = rows.reduce((s, p) => s + p.net, 0);
    return {
      id,
      label: categoryLabel(id, catalog),
      color: categoryColor(id, ids),
      purchases: rows.reduce((s, p) => s + p.purchases, 0),
      refunds: rows.reduce((s, p) => s + p.refunds, 0),
      buyers: buyers.size,
      products: rows.filter((p) => p.purchases > 0).length,
      spent: rows.reduce((s, p) => s + p.spentSeasonal + p.spentClassic, 0),
      refunded: rows.reduce((s, p) => s + p.refunded, 0),
      net,
      share: totalNet > 0 ? Math.max(0, net) / totalNet : 0,
    };
  });
}

// ---------- Par jour ----------

export interface ShopDayByCategory {
  date: string;
  buyers: number;
  /** Points gardés par catégorie (un jour où tout est rendu peut finir à 0). */
  points: Record<string, number>;
  /** Achats gardés par catégorie. */
  purchases: Record<string, number>;
}

/** Chaque jour réparti par catégorie (d'après la clé de produit du satellite). */
export function dailyByCategory(daily: readonly ShopDayDTO[], catalog: ShopCatalog | null): ShopDayByCategory[] {
  return daily.map((d) => {
    const points: Record<string, number> = {};
    const purchases: Record<string, number> = {};
    for (const [key, v] of Object.entries(d.products ?? {})) {
      const sep = key.indexOf(':');
      const kind = (sep > 0 ? key.slice(0, sep) : 'product') as ShopKind;
      const id = sep > 0 ? key.slice(sep + 1) : key;
      const c = categoryOf(kind, id, catalog);
      points[c] = (points[c] ?? 0) + v.points;
      purchases[c] = (purchases[c] ?? 0) + v.purchases;
    }
    return { date: d.date, buyers: d.buyers, points, purchases };
  });
}

// ---------- Tri des tableaux ----------

export type SortDir = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

/**
 * Nouveau tri après un clic sur une colonne : la même colonne change de sens ; une autre commence par le plus grand
 * (nombres) ou par A (textes).
 */
export function nextSort<K extends string>(current: SortState<K>, key: K, textual: boolean): SortState<K> {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: textual ? 'asc' : 'desc' };
}

/** Lignes triées sur une valeur (nombre ou texte), sans modifier la liste ; à égalité, par nom. */
export function sortRows<T extends { name?: string; label?: string }>(
  rows: readonly T[], value: (row: T) => number | string | null, dir: SortDir,
): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  const nameOf = (r: T) => r.name ?? r.label ?? '';
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va == null && vb == null) return nameOf(a).localeCompare(nameOf(b), 'fr');
    if (va == null) return 1;
    if (vb == null) return -1;
    const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'fr');
    return c !== 0 ? sign * c : nameOf(a).localeCompare(nameOf(b), 'fr');
  });
}
